import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import MyProfile from './myprofilenonav';
import { AnimatePresence, motion } from 'framer-motion';

import {
  AcademicCapIcon,
  BanknotesIcon,
  CheckBadgeIcon,
  ClockIcon,
  ReceiptRefundIcon,
  UsersIcon,
  SparklesIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { getProfilePicSrc } from '../utils/mediaUrl';


const taskIconPalette = [
  { icon: ClockIcon,         fg: 'text-teal-700',   bg: 'bg-teal-50',   grad: 'from-teal-400 to-cyan-500'       },
  { icon: CheckBadgeIcon,    fg: 'text-purple-700',  bg: 'bg-purple-50', grad: 'from-purple-400 to-violet-500'   },
  { icon: UsersIcon,         fg: 'text-sky-700',     bg: 'bg-sky-50',    grad: 'from-sky-400 to-blue-500'        },
  { icon: BanknotesIcon,     fg: 'text-amber-700',   bg: 'bg-amber-50',  grad: 'from-yellow-400 to-amber-500'    },
  { icon: ReceiptRefundIcon, fg: 'text-rose-700',    bg: 'bg-rose-50',   grad: 'from-rose-400 to-pink-500'       },
  { icon: AcademicCapIcon,   fg: 'text-indigo-700',  bg: 'bg-indigo-50', grad: 'from-indigo-400 to-blue-600'     },
];

const cx = (...classes) => classes.filter(Boolean).join(' ');
const normalizeStr = (v = '') => String(v || '').trim().toLowerCase();

/* ─── Typewriter hook ─────────────────────────────────────────────────────── */
function useTypewriter(lines, active) {
  const [displayed, setDisplayed] = useState([]);
  const [lineIdx, setLineIdx]     = useState(0);
  const [charIdx, setCharIdx]     = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) { setDisplayed([]); setLineIdx(0); setCharIdx(0); return; }
    if (lineIdx >= lines.length) return;

    const current = lines[lineIdx];
    if (charIdx < current.length) {
      timerRef.current = setTimeout(() => {
        setDisplayed((prev) => {
          const next = [...prev];
          next[lineIdx] = (next[lineIdx] || '') + current[charIdx];
          return next;
        });
        setCharIdx((c) => c + 1);
      }, 26);
    } else {
      timerRef.current = setTimeout(() => {
        setLineIdx((l) => l + 1);
        setCharIdx(0);
      }, 280);
    }
    return () => clearTimeout(timerRef.current);
  }, [active, lines, lineIdx, charIdx]);

  return displayed;
}

/* ─── OrgTree embedded component ─────────────────────────────────────────── */
function OrgTreeEmbed({ navigate }) {
  const [departments, setDepartments] = useState([]);
  const [staff, setStaff]             = useState([]);
  const [company, setCompany]         = useState([]);
  const [busy, setBusy]               = useState(true);

  useEffect(() => {
    const load = async () => {
      setBusy(true);
      try {
        const [dR, sR, cR] = await Promise.all([
          fetch('https://api.insightos.co.uk/api/getdepartments', { credentials: 'include' }),
          fetch('https://api.insightos.co.uk/api/staff',          { credentials: 'include' }),
          fetch('https://api.insightos.co.uk/api/company',        { credentials: 'include' }),
        ]);
        if (dR.ok) { const d = await dR.json(); setDepartments(Array.isArray(d?.data) ? d.data : []); }
        if (sR.ok) { const d = await sR.json(); setStaff(Array.isArray(d?.data) ? d.data : []); }
        if (cR.ok) { const d = await cR.json(); setCompany(Array.isArray(d?.data) ? d.data : []); }
      } catch { /* silent */ }
      finally  { setBusy(false); }
    };
    load();
  }, []);

  const byDept = departments.map((dept) => ({
    ...dept,
    members: staff.filter((p) => p.Department === dept.Name),
  }));

  if (busy) {
    return (
      <div className="flex items-center justify-center py-16 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          className="h-8 w-8 rounded-full border-4 border-cyan-400 border-t-transparent"
        />
      </div>
    );
  }

  return (
    <div className="min-h-[500px] bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-6 space-y-5">
      {company[0] && (
        <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🏢</span>
              <p className="text-xl font-bold text-white">{company[0].CompanyName}</p>
            </div>
            <div className="flex flex-wrap gap-3 ml-auto">
              {[['Staff', staff.length], ['Departments', departments.length]].map(([label, val]) => (
                <div key={label} className="rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-center backdrop-blur-sm">
                  <p className="text-xs text-blue-200">{label}</p>
                  <p className="text-sm font-bold text-white">{val}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {byDept.length === 0 ? (
        <p className="rounded-xl border border-white/20 bg-white/10 p-5 text-center text-sm text-blue-200">
          No departments found.
        </p>
      ) : byDept.map((dept, di) => (
        <motion.div
          key={dept.Name}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: di * 0.07, duration: 0.35 }}
          className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <span>🏬</span>
            <p className="font-bold text-cyan-300">{dept.Name}</p>
            <span className="ml-auto rounded-full bg-cyan-400/20 px-2.5 py-0.5 text-xs font-bold text-cyan-300">{dept.members.length}</span>
          </div>
          {dept.members.length === 0 ? (
            <p className="text-xs text-blue-300">No staff assigned.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {dept.members.map((person, pi) => {
                const pic = getProfilePicSrc(person);
                return (
                  <motion.div
                    key={person.Name || pi}
                    initial={{ opacity: 0, scale: 0.88 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: di * 0.07 + pi * 0.04, duration: 0.25 }}
                    className="flex flex-col items-center rounded-xl border border-white/10 bg-white/10 p-4 text-center backdrop-blur-sm hover:bg-white/15 transition-colors"
                  >
                    {pic ? (
                      <img src={pic} alt={person.Name} className="h-12 w-12 rounded-full object-cover ring-2 ring-cyan-400/50" />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 text-base font-bold text-white ring-2 ring-cyan-400/50">
                        {String(person.Name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <p className="mt-2 text-xs font-bold text-white line-clamp-1">{person.Name}</p>
                    <p className="text-xs text-cyan-200">{person.Role}</p>
                    <button
                      onClick={() => navigate('/chatdashboard')}
                      className="mt-2 w-full rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 py-1 text-xs font-semibold text-white hover:from-emerald-600 hover:to-green-700 transition-colors"
                    >
                      Message
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
}

/* ─── Tasks step (typewriter) ─────────────────────────────────────────────── */
function TasksStep({ taskCards, tasksLoading, navigate }) {
  const typedLines = useTypewriter(taskCards.map((t) => t.title), !tasksLoading);

  return (
    <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
      {/* Colourful header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-8 py-10 text-white">
        <motion.div className="absolute -top-10 -right-10 h-48 w-48 rounded-full bg-white/10 blur-3xl"
          animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="relative z-10">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm"
          >
            <CheckBadgeIcon className="h-3.5 w-3.5" /> Your Role
          </motion.div>
          <h2 className="text-3xl font-extrabold sm:text-4xl">Your Tasks</h2>
          <p className="mt-2 text-orange-100">Key responsibilities — watch them appear below.</p>
        </div>
      </div>

      <div className="p-6 sm:p-8">
        {tasksLoading && (
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="h-4 w-4 rounded-full border-2 border-orange-500 border-t-transparent" />
            Loading your tasks…
          </div>
        )}

        {!tasksLoading && taskCards.length === 0 && (
          <p className="rounded-xl border border-gray-200 bg-gray-50 p-5 text-sm text-gray-600">
            No tasks have been assigned to your role yet.
          </p>
        )}

        {!tasksLoading && taskCards.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {taskCards.map((task, i) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: i * 0.07, duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow cursor-default"
              >
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${task.grad}`} />
                <span className={cx(task.bg, task.fg, 'inline-flex rounded-xl p-3 shadow-sm')}>
                  <task.icon className="h-6 w-6" />
                </span>
                <p className="mt-4 min-h-[1.5rem] text-sm font-bold text-gray-900">
                  {typedLines[i] ?? ''}
                  {typedLines[i] !== undefined && typedLines[i].length < task.title.length && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.55, repeat: Infinity }}
                      className="ml-0.5 inline-block h-[1em] w-0.5 align-middle bg-gray-800"
                    />
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Knowledge Tree CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-violet-50 to-purple-50 p-5"
        >
          <p className="font-bold text-indigo-900">📚 Knowledge Tree</p>
          <p className="mt-1 text-sm text-indigo-700">
            Find process guides and step-by-step how-tos for your role.
          </p>
          <button
            onClick={() => navigate('/knowledge-tree')}
            className="mt-3 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:from-indigo-700 hover:to-violet-700 transition-all"
          >
            Open Knowledge Tree →
          </button>
        </motion.div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Main component ─────────────────────────────── */
export default function OnboardStaff() {
  const user     = useContext(AuthContext);
  const navigate = useNavigate();

  const [step, setStep]                   = useState(() => {
    const saved = parseInt(localStorage.getItem('onboardStep') || '0', 10);
    return isNaN(saved) ? 0 : saved;
  });
  const [direction, setDirection]         = useState(1);
  const [companyName, setCompanyName]     = useState('Your Company');
  const [staff, setStaff]                 = useState([]);
  const [roleTaskRows, setRoleTaskRows]   = useState([]);
  const [trainingDocuments, setTrainingDocuments] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [tasksLoading, setTasksLoading]   = useState(false);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [savingComplete, setSavingComplete] = useState(false);
  const [savingModal, setSavingModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  /* ── Data fetch ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setTrainingLoading(true);
      setStatusMessage('');
      try {
        const [companyRes, staffRes, trainingRes, trainingResultsRes] = await Promise.all([
          fetch('https://api.insightos.co.uk/api/company',           { credentials: 'include' }),
          fetch('https://api.insightos.co.uk/api/staff',             { credentials: 'include' }),
          fetch('https://api.insightos.co.uk/api/trainingdocuments', { credentials: 'include' }),
          fetch('https://api.insightos.co.uk/api/trainingresults',   { credentials: 'include' }),
        ]);

        if (companyRes.ok) {
          const d = await companyRes.json();
          if (d?.data?.[0]?.CompanyName) setCompanyName(d.data[0].CompanyName);
        }

        if (staffRes.ok) {
          const d = await staffRes.json();
          setStaff(Array.isArray(d?.data) ? d.data : []);
        }

        if (trainingRes.ok && trainingResultsRes.ok) {
          const trainingData   = await trainingRes.json();
          const resultsData    = await trainingResultsRes.json();
          const trainingRows   = Array.isArray(trainingData?.data)   ? trainingData.data   : [];
          const trainingResults = Array.isArray(resultsData?.data)   ? resultsData.data    : [];

          const grouped = trainingRows.reduce((acc, item) => {
            const { TrainingDocName, Title, Page, Code, id, Role } = item;
            if (!TrainingDocName) return acc;
            if (!acc[TrainingDocName]) acc[TrainingDocName] = { TrainingDocName, Role, Pages: [] };
            acc[TrainingDocName].Pages.push({ Title, Page, Code, id });
            return acc;
          }, {});

          const groupedArray = Object.values(grouped).map((group) => {
            const sortedPages = [...group.Pages].sort((a, b) => a.Page - b.Page);
            const firstPageId = sortedPages[0]?.id;
            const matchedResult = trainingResults
              .filter((r) => String(r?.CourseId) === String(firstPageId))
              .sort((a, b) => Number(b?.Attempts || 0) - Number(a?.Attempts || 0))[0];
            return {
              ...group,
              Pages:          sortedPages,
              isComplete:     matchedResult?.Completed === 1,
              score:          matchedResult?.Result,
              difficulty:     matchedResult?.Difficulty,
              QuestionNumber: matchedResult?.QuestionNumber,
            };
          });

          setTrainingDocuments(groupedArray);
        }
      } catch {
        setStatusMessage('Some onboarding data could not be loaded. You can still continue.');
      } finally {
        setLoading(false);
        setTrainingLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const roleName = normalizeStr(user?.role);
    if (!roleName) { setRoleTaskRows([]); return; }
    const loadRoleTasks = async () => {
      setTasksLoading(true);
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/taskrole/${encodeURIComponent(roleName)}`, { credentials: 'include' });
        if (!response.ok) { setRoleTaskRows([]); return; }
        const data = await response.json();
        setRoleTaskRows(Array.isArray(data?.data) ? data.data : []);
      } catch { setRoleTaskRows([]); }
      finally  { setTasksLoading(false); }
    };
    loadRoleTasks();
  }, [user?.role]);

  /* ── Derived ────────────────────────────────────────────────────────────── */
  const normalizedDepartment = normalizeStr(user?.department);
  const userRole             = normalizeStr(user?.role);

  const teamByDepartment = useMemo(() =>
    staff.filter((p) => normalizeStr(p?.Department) === normalizedDepartment),
  [staff, normalizedDepartment]);

  const onboardingTeamMembers = useMemo(() => {
    const source = teamByDepartment.length > 0 ? teamByDepartment : staff;
    return source.slice(0, 12).map((person, index) => {
      const firstName = String(person?.FirstName || '').trim();
      const lastName  = String(person?.LastName  || '').trim();
      const fullName  = `${firstName} ${lastName}`.trim();
      const fallback  = String(person?.Name || person?.Email || '').trim();
      const name      = fullName || fallback || `Team Member ${index + 1}`;
      const role      = String(person?.Role || person?.Position || person?.Access || person?.Department || 'Team Member').trim();
      return {
        id:       person?.id || person?.ID || person?.Email || `${name}-${index}`,
        name,
        role,
        imageUrl: getProfilePicSrc(person),
      };
    });
  }, [teamByDepartment, staff]);

  const roleTasks = useMemo(() => {
    const collected = [];
    roleTaskRows.forEach((row) => {
      [row?.Task, row?.Name, row?.tasks, row?.Description].forEach((v) => { if (v) collected.push(String(v)); });
    });
    return Array.from(new Set(
      collected.flatMap((e) => e.split(',')).map((e) => e.trim()).filter(Boolean)
    ));
  }, [roleTaskRows]);

  const taskCards = useMemo(() =>
    roleTasks.map((task, index) => {
      const p = taskIconPalette[index % taskIconPalette.length];
      return { id: `${task}-${index}`, title: task, icon: p.icon, fg: p.fg, bg: p.bg, grad: p.grad };
    }),
  [roleTasks]);

  const myTrainingCourses = useMemo(() =>
    trainingDocuments.filter((doc) => normalizeStr(doc?.Role) === userRole),
  [trainingDocuments, userRole]);

  /* ── Steps config ──────────────────────────────────────────────────────── */
  const onboardingSteps = [
    'Welcome',
    'My Profile',
    'Meet Your Team',
    'Your Tasks',
    'Task Calendar',
    'Training',
    'Our Structure',
    'Clock In',
    'AI Assistant',
    'News Feed',
    'Meetings',
    'Holiday',
    'Get Started',
  ];
  const totalSteps = onboardingSteps.length;

  const go = useCallback((next) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
    localStorage.setItem('onboardStep', String(next));
  }, [step]);

  const completeOnboarding = async () => {
    setSavingComplete(true);
    setStatusMessage('');
    try {
      const response = await fetch('https://api.insightos.co.uk/api/onboardstaff/complete', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) throw new Error(data.message || 'Failed to complete onboarding');
      localStorage.removeItem('onboardStep');
      setSavingModal(true);
    } catch (error) {
      setStatusMessage(error.message || 'Failed to complete onboarding');
    } finally {
      setSavingComplete(false);
    }
  };

  /* ── Slide variants ─────────────────────────────────────────────────────── */
  const slideVariants = {
    enter:  (d) => ({ opacity: 0, x: d > 0 ? 64 : -64 }),
    center: { opacity: 1, x: 0,  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
    exit:   (d) => ({ opacity: 0, x: d > 0 ? -64 : 64, transition: { duration: 0.22, ease: 'easeIn' } }),
  };

  /* ── Step content ───────────────────────────────────────────────────────── */
  const renderStepContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-24">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
            className="h-10 w-10 rounded-full border-4 border-indigo-600 border-t-transparent"
          />
        </div>
      );
    }

    /* ── 0: Welcome ─────────────────────────────────────────────────────── */
    if (step === 0) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-lg">
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-violet-600 to-blue-700 px-8 py-16 text-white">
            <motion.div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl"
              animate={{ scale: [1, 1.18, 1] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }} />
            <motion.div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-pink-500/20 blur-3xl"
              animate={{ scale: [1, 1.22, 1] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }} />

            <div className="relative z-10">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}
                className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest backdrop-blur-sm"
              >
                <SparklesIcon className="h-3.5 w-3.5" /> Staff Onboarding
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}
                className="text-4xl font-extrabold tracking-tight sm:text-5xl"
              >
                Welcome to {companyName}
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.5 }}
                className="mt-4 max-w-2xl text-lg text-indigo-100"
              >
                A guided setup to get you productive from day one. Just a few steps to get fully set up.
              </motion.p>
            </div>
          </div>

          <div className="bg-white px-8 py-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { icon: '👋', label: 'Meet your team',         color: 'from-indigo-500 to-violet-500' },
                { icon: '✅', label: 'Complete your profile',  color: 'from-emerald-500 to-teal-500'  },
                { icon: '🚀', label: 'Start your role tasks',  color: 'from-orange-500 to-amber-500'  },
              ].map((item, i) => (
                <motion.div key={item.label}
                  initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 + i * 0.12, duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className={cx('flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-2xl shadow-md', item.color)}>
                    {item.icon}
                  </div>
                  <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    /* ── 1: My Profile ──────────────────────────────────────────────────── */
    if (step === 1) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-8 py-7">
            <h2 className="text-2xl font-extrabold text-white">Complete your profile</h2>
            <p className="mt-1 text-sm text-violet-100">Fill in your details so your team can get to know you.</p>
          </div>
          <div className="p-4">
            <MyProfile onboardMode />
          </div>
        </div>
      );
    }

    /* ── 2: Meet Your Team ──────────────────────────────────────────────── */
    if (step === 2) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          <div className="relative overflow-hidden bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600 px-8 py-10 text-white">
            <motion.div className="absolute -top-10 right-0 h-48 w-48 rounded-full bg-white/10 blur-3xl"
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }} />
            <div className="relative z-10">
              <h2 className="text-3xl font-extrabold">Meet your team</h2>
              <p className="mt-2 text-blue-100">
                {user?.department ? `${user.department} ·` : ''} {teamByDepartment.length || onboardingTeamMembers.length} colleague{(teamByDepartment.length || onboardingTeamMembers.length) !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {onboardingTeamMembers.length === 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
                No team members found yet — they'll appear here once staff have been added.
              </div>
            ) : (
              <ul role="list" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {onboardingTeamMembers.map((person, i) => (
                  <motion.li
                    key={person.id}
                    initial={{ opacity: 0, y: 40, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.42, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                    className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 p-6 shadow-sm ring-1 ring-indigo-100 hover:shadow-md hover:ring-indigo-300 transition-all duration-200 cursor-default"
                  >
                    <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br from-indigo-400/20 to-violet-400/20 blur-2xl" />
                    <div className="relative flex flex-col items-center text-center">
                      {person.imageUrl ? (
                        <img alt={person.name} src={person.imageUrl}
                          className="h-20 w-20 rounded-full object-cover ring-4 ring-white shadow-md" />
                      ) : (
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-2xl font-bold text-white ring-4 ring-white shadow-md">
                          {String(person.name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <p className="mt-4 text-sm font-bold text-gray-900">{person.name}</p>
                      <span className="mt-1 inline-flex rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">
                        {person.role}
                      </span>
                    </div>
                  </motion.li>
                ))}
              </ul>
            )}
          </div>
        </div>
      );
    }

    /* ── 3: Tasks (typewriter) ──────────────────────────────────────────── */
    if (step === 3) {
      return <TasksStep taskCards={taskCards} tasksLoading={tasksLoading} navigate={navigate} />;
    }

    /* ── 4: Task Calendar ───────────────────────────────────────────────── */
    if (step === 4) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          <div className="relative overflow-hidden bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-6 py-8 text-white sm:px-8 sm:py-10">
            <motion.div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-3xl"
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
            <div className="relative z-10">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm"
              >
                📋 Daily Workflow
              </motion.div>
              <h2 className="text-2xl font-extrabold sm:text-3xl">Your Task Calendar</h2>
              <p className="mt-2 text-sm text-orange-100 sm:text-base">
                Every shift, your tasks are laid out on a live timeline. Complete them, log issues, and track progress in real time.
              </p>
            </div>
          </div>
          <div className="p-6 sm:p-8 space-y-5">
            <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-600 text-white text-lg">📅</span>
                <h3 className="text-base font-bold text-amber-900">How the Daily Task Calendar works</h3>
              </div>
              <ol className="space-y-2 text-sm text-amber-800 list-decimal list-inside">
                <li>Your tasks are loaded automatically each shift based on your role.</li>
                <li>The current task is highlighted — earlier tasks show as completed.</li>
                <li>Click <strong>Complete Task</strong> when finished — this is logged for your manager.</li>
                <li>If you're struggling, click <strong>Raise an Issue</strong> to alert your manager or ask AI for help.</li>
              </ol>
            </div>

            {/* Static task card preview */}
            <div className="pointer-events-none select-none space-y-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Preview</p>
              <div className="relative overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-md">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-orange-500">Current Task · 09:00 – 10:00</p>
                      <p className="mt-1 text-base font-bold text-gray-900">Morning Briefing & Handover</p>
                      <p className="mt-1 text-xs text-gray-500">Review overnight updates and brief the incoming team.</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">In Progress</span>
                  </div>
                  <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full w-3/5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500" />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-2 text-xs font-bold text-white shadow-sm">✓ Complete Task</span>
                    <span className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-bold text-rose-700">⚠ Raise an Issue</span>
                  </div>
                </div>
              </div>
              {/* Raise issue form preview */}
              <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4">
                <p className="text-xs font-bold text-rose-700 mb-3 uppercase tracking-wide">⚠ Raise an Issue — example form</p>
                <div className="space-y-2">
                  <div className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs text-gray-400">Describe your struggle...</div>
                  <div className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs text-gray-400">Your suggested resolution...</div>
                  <div className="flex gap-2 mt-1">
                    <span className="rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 px-3 py-1.5 text-xs font-bold text-white">Ask Manager</span>
                    <span className="rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 px-3 py-1.5 text-xs font-bold text-white">Ask AI</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center pt-2">
              <button
                onClick={() => navigate('/taskcalendartoday')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-3 text-sm font-bold text-white shadow-md hover:from-amber-600 hover:to-orange-700 transition-all"
              >
                Open Task Calendar →
              </button>
            </div>
          </div>
        </div>
      );
    }

    /* ── 5: Training ────────────────────────────────────────────────────── */
    if (step === 5) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          <div className="relative overflow-hidden bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-10 text-white">
            <motion.div className="absolute -right-10 top-0 h-48 w-48 rounded-full bg-white/10 blur-3xl"
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
            <div className="relative z-10">
              <h2 className="text-3xl font-extrabold">Your Training</h2>
              <p className="mt-2 text-emerald-100">Courses aligned to your role to hit the ground running.</p>
            </div>
          </div>
          <div className="p-6 sm:p-8">
            {trainingLoading && (
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="h-4 w-4 rounded-full border-2 border-emerald-500 border-t-transparent" />
                Loading courses…
              </div>
            )}
            {!trainingLoading && myTrainingCourses.length === 0 && (
              <p className="rounded-xl border border-gray-200 bg-gray-50 p-5 text-sm text-gray-600">
                No courses assigned to your role yet.
              </p>
            )}
            {!trainingLoading && myTrainingCourses.length > 0 && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {myTrainingCourses.map((course, i) => {
                  const pct    = course.QuestionNumber ? Math.round(((course.score || 0) / course.QuestionNumber) * 100) : 0;
                  const isFail = course.isComplete && pct < 80;
                  return (
                    <motion.div key={course.TrainingDocName}
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.08, duration: 0.35 }}
                      className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 to-teal-500" />
                      <p className="font-bold text-gray-900">{course.TrainingDocName}</p>
                      <p className="mt-0.5 text-xs text-gray-500">Role: {course.Role || 'General'}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={cx('rounded-full px-3 py-1 text-xs font-semibold', course.isComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>
                          {course.isComplete ? '✓ Completed' : 'Not started'}
                        </span>
                        {course.isComplete && (
                          <span className={cx('rounded-full px-3 py-1 text-xs font-semibold', isFail ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700')}>
                            {pct}% — {isFail ? 'Fail' : 'Pass'}
                          </span>
                        )}
                      </div>
                      <button onClick={() => navigate('/training')}
                        className="mt-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:from-emerald-600 hover:to-teal-700 transition-colors"
                      >
                        {isFail ? 'Retake Course' : 'Open Course'} →
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    /* ── 6: Org Structure ───────────────────────────────────────────────── */
    if (step === 6) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-10 text-white">
            <h2 className="text-3xl font-extrabold">Your Organisation</h2>
            <p className="mt-2 text-blue-100">See how the team is structured and who's in each department.</p>
          </div>
          <OrgTreeEmbed navigate={navigate} />
        </div>
      );
    }

    /* ── 7: Clock In ────────────────────────────────────────────────────── */
    if (step === 7) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          <div className="relative overflow-hidden bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-10 text-white">
            <motion.div className="absolute -bottom-10 -right-10 h-48 w-48 rounded-full bg-white/10 blur-3xl"
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
            <div className="relative z-10">
              <h2 className="text-3xl font-extrabold">Clocking In</h2>
              <p className="mt-2 text-orange-100">When you log in for a shift, a clock-in prompt appears automatically.</p>
            </div>
          </div>
          <div className="p-8">
            <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-6 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-500">Preview</p>
              <p className="mt-3 text-xl font-bold text-gray-900">Hello {user?.name || 'there'}! Your shift is starting.</p>
              <p className="mt-1 text-sm text-gray-600">Ready to clock in and begin?</p>
              <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
                <ClockIcon className="h-4 w-4" />
                Clock In Now
              </div>
            </div>
          </div>
        </div>
      );
    }

    /* ── 8: AI Assistant ────────────────────────────────────────────────── */
    if (step === 8) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          <div className="relative overflow-hidden bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 px-6 py-8 text-white sm:px-8 sm:py-10">
            <motion.div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-3xl"
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
            <div className="relative z-10">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm"
              >
                🌐 Dashboard Support
              </motion.div>
              <h2 className="text-2xl font-extrabold sm:text-3xl">AI Assistant</h2>
              <p className="mt-2 max-w-2xl text-sm text-purple-100 sm:text-base">
                The AI Assistant lives inside <strong>Dashboard → Support</strong>. It gives you live information about your company, your work, and what is happening across InsightOS.
              </p>
            </div>
          </div>
          <div className="p-6 sm:p-8 space-y-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-violet-100 bg-violet-50/70 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-600 text-white text-lg shadow-sm">💬</span>
                  <h3 className="text-base font-bold text-violet-950">Where to find it</h3>
                </div>
                <ol className="space-y-2 text-sm text-violet-900 list-decimal list-inside">
                  <li>Open your <strong>Dashboard</strong> and go to <strong>Support</strong>.</li>
                  <li>On <strong>desktop</strong>, use the blue globe in the <strong>right-hand corner</strong>.</li>
                  <li>On <strong>mobile</strong>, use the blue globe in the <strong>top bar</strong>.</li>
                  <li>Ask a question and the assistant will reply with live company information.</li>
                </ol>
              </div>

              <div className="rounded-2xl border border-sky-100 bg-sky-50/70 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-600 text-white text-lg shadow-sm">⚡</span>
                  <h3 className="text-base font-bold text-sky-950">What it can tell you</h3>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {[
                    'Your tasks and priorities',
                    'Today\'s meetings',
                    'Holiday and absence status',
                    'Team snapshot and bottlenecks',
                    'Company news and updates',
                    'Tomorrow\'s schedule',
                  ].map((item) => (
                    <div key={item} className="rounded-xl border border-white/80 bg-white px-3 py-2 text-sm font-medium text-sky-900 shadow-sm">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 shadow-2xl">
              <div className="flex items-center justify-between border-b border-white/10 bg-gradient-to-r from-sky-500/20 via-blue-500/20 to-indigo-500/20 px-4 py-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-200">Live Snapshot</p>
                  <p className="text-sm font-bold text-white">Blue Globe Assistant</p>
                </div>
                <span className="rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 text-[11px] font-semibold text-sky-200">
                  Dashboard Support
                </span>
              </div>
              <div className="space-y-4 p-5 font-mono text-xs leading-relaxed text-slate-200 sm:text-sm">
                <div className="flex justify-end">
                  <div className="max-w-xl rounded-2xl bg-sky-500/15 px-4 py-3 text-sky-100 ring-1 ring-sky-400/20">
                    What does my day look like?
                  </div>
                </div>
                <div className="max-w-2xl rounded-2xl bg-white/5 px-4 py-3 ring-1 ring-white/10">
                  <p><span className="text-sky-300">AI:</span> You start with 3 priority tasks, 1 meeting at 10:30, and no holiday conflicts today.</p>
                  <p className="mt-2"><span className="text-sky-300">AI:</span> Latest company update: a new policy note was posted in the News Feed this morning.</p>
                  <p className="mt-2"><span className="text-sky-300">AI:</span> Your team currently has 1 open bottleneck waiting for manager review.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-3">Useful Support Areas</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[
                  { icon: '🌐', label: 'AI Assistant' },
                  { icon: '🏠', label: 'Getting Started' },
                  { icon: '📅', label: 'Leave & Absence' },
                  { icon: '📋', label: 'Tasks & Calendar' },
                  { icon: '📰', label: 'Company News' },
                  { icon: '🔒', label: 'Security & Privacy' },
                ].map(({ icon, label }) => (
                  <div key={label} className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm">
                    <span className="text-base">{icon}</span>{label}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center pt-2">
              <button
                onClick={() => navigate('/support')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-md hover:from-violet-700 hover:to-indigo-700 transition-all"
              >
                Open Support →
              </button>
            </div>
          </div>
        </div>
      );
    }

    /* ── 9: News Feed ────────────────────────────────────────────────────── */
    if (step === 9) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          {/* Header */}
          <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 text-white sm:px-8 sm:py-10">
            <motion.div className="absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-white/10 blur-3xl"
              animate={{ scale: [1, 1.18, 1] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }} />
            <div className="relative z-10">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm"
              >
                📰 Social Feed
              </motion.div>
              <h2 className="text-2xl font-extrabold sm:text-3xl">News Feed</h2>
              <p className="mt-2 text-sm text-indigo-100 sm:text-base">
                Stay connected with your team — see announcements, shout-outs, and company news all in one place.
              </p>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-5">
            {/* How it works */}
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-white text-lg">📖</span>
                <h3 className="text-base font-bold text-indigo-900">How to use the News Feed</h3>
              </div>
              <ol className="space-y-2 text-sm text-indigo-800 list-decimal list-inside">
                <li>Open <strong>News Feed</strong> from the navigation menu.</li>
                <li>Switch between <strong>Team</strong>, <strong>Everyone</strong>, and <strong>Product Hub</strong> tabs to filter posts.</li>
                <li>Like and comment on posts — reactions are visible to your whole team.</li>
                <li>Managers can create posts with shout-outs, news, bulletins, and events.</li>
              </ol>
            </div>

            {/* Static mockup of newshome */}
            <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm pointer-events-none select-none">
              {/* Mock header bar */}
              <div className="flex items-center justify-between bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 border-b border-indigo-200/60 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-gray-900 flex items-center justify-center text-white text-xs font-bold">IS</div>
                  <span className="text-base font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">News Feed</span>
                </div>
                <span className="rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-3 py-1.5 text-xs font-semibold text-white">Create Post</span>
              </div>
              {/* Mock tabs */}
              <div className="flex border-b border-indigo-100 bg-white/90 px-4">
                {['Team', 'Everyone', 'Product Hub'].map((t, i) => (
                  <span key={t} className={`px-4 py-2.5 text-xs font-semibold border-b-2 ${i === 1 ? 'border-indigo-500 text-indigo-900' : 'border-transparent text-indigo-500'}`}>{t}</span>
                ))}
              </div>
              {/* Mock "People" row */}
              <div className="bg-gradient-to-r from-indigo-50/80 via-white to-purple-50/80 px-4 py-3 border-b border-indigo-100">
                <p className="text-xs font-semibold text-indigo-700 mb-2">People</p>
                <div className="flex gap-3">
                  {[
                    { initials: 'SJ', color: 'bg-sky-500', name: 'Sarah J.' },
                    { initials: 'MC', color: 'bg-violet-500', name: 'Mike C.' },
                    { initials: 'AL', color: 'bg-rose-500', name: 'Amy L.' },
                    { initials: 'TO', color: 'bg-amber-500', name: 'Tom O.' },
                  ].map(({ initials, color, name }) => (
                    <div key={initials} className="flex flex-col items-center gap-1">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${color} text-white text-xs font-bold ring-2 ring-white`}>{initials}</div>
                      <span className="text-[10px] text-gray-600 font-medium">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Mock posts */}
              <div className="bg-white px-4 py-3 space-y-3">
                {[
                  { initials: 'MG', color: 'bg-indigo-500', author: 'Maria G.', role: 'Manager', time: '2h ago', title: '🎉 Welcome to the team!', body: 'Huge welcome to our newest members joining this week. Excited to have you on board!', likes: 12, comments: 3, tag: 'Everyone' },
                  { initials: 'RK', color: 'bg-emerald-500', author: 'Ryan K.', role: 'HR', time: '5h ago', title: '📅 Team Meeting — Friday 10am', body: 'Reminder: all-hands meeting this Friday at 10am in the main conference room. Please confirm attendance.', likes: 7, comments: 1, tag: 'Team' },
                ].map(({ initials, color, author, role, time, title, body, likes, comments, tag }) => (
                  <div key={author} className="rounded-2xl border border-indigo-100/60 bg-gradient-to-r from-indigo-50/40 via-white to-purple-50/40 p-4 shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${color} text-white text-xs font-bold`}>{initials}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 leading-tight">{author} <span className="font-normal text-gray-500 text-xs">· {role}</span></p>
                        <p className="text-xs text-gray-400">{time}</p>
                      </div>
                      <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">{tag}</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 mb-1">{title}</p>
                    <p className="text-xs text-gray-600 leading-relaxed">{body}</p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
                      <span>👍 {likes}</span>
                      <span>💬 {comments}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: '🔔', title: 'Notifications', desc: 'Get notified when posts are made or you\'re mentioned.' },
                { icon: '🏆', title: 'Shout-outs', desc: 'Managers can shout out team members in posts.' },
                { icon: '🔒', title: 'Scoped Posts', desc: 'Posts can target Everyone, your Team, or Managers only.' },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="flex gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <span className="text-2xl">{icon}</span>
                  <div>
                    <p className="text-sm font-bold text-gray-800">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center pt-2">
              <button
                onClick={() => navigate('/newshome')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-3 text-sm font-bold text-white shadow-md hover:from-indigo-700 hover:to-violet-700 transition-all"
              >
                Open News Feed →
              </button>
            </div>
          </div>
        </div>
      );
    }

    /* ── 10: Meetings ──────────────────────────────────────────────────────── */
    if (step === 10) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          <div className="relative overflow-hidden bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600 px-6 py-8 text-white sm:px-8 sm:py-10">
            <motion.div className="absolute -right-10 top-0 h-48 w-48 rounded-full bg-white/10 blur-3xl"
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
            <div className="relative z-10">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm"
              >
                📅 Scheduling
              </motion.div>
              <h2 className="text-2xl font-extrabold sm:text-3xl">Meetings & Calendar</h2>
              <p className="mt-2 text-sm text-sky-100 sm:text-base">
                Schedule meetings, run them from the platform, and capture notes with AI-powered speech-to-text.
              </p>
            </div>
          </div>
          <div className="p-6 sm:p-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: '📆', title: 'Meetings Calendar', desc: 'View all your scheduled meetings. Navigate by month and click a day to see what\'s on.' },
                { icon: '📋', title: 'Meeting Viewer', desc: 'Open any meeting to see the agenda, attendees, and links. Start and end meetings directly.' },
                { icon: '🎙️', title: 'Note Taker', desc: 'Capture meeting notes with live speech-to-text. Auto-saves a local draft so nothing is lost.' },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="rounded-2xl border border-sky-100 bg-sky-50/60 p-4">
                  <span className="text-2xl">{icon}</span>
                  <p className="mt-2 text-sm font-bold text-sky-900">{title}</p>
                  <p className="mt-1 text-xs text-sky-700">{desc}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5">
              <div className="flex items-start gap-3">
                <span className="text-2xl shrink-0">📹</span>
                <div>
                  <p className="text-sm font-bold text-emerald-900">Video Calling — Google Meet</p>
                  <p className="mt-1 text-sm text-emerald-800">
                    For online meetings, activate <strong>Google Meet</strong>. When a meeting's location is set to a Google Meet link, it opens directly from the meeting viewer. Make sure Google Meet is enabled on your Google account before your first video call.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white shadow text-lg">🗓️</div>
                <div>
                  <p className="text-sm font-bold text-blue-900">Connect Google Calendar</p>
                  <p className="text-xs text-blue-700">Sync your meetings with Google Calendar to stay on top of your schedule across all devices.</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/meetingscalendar')}
                className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2.5 text-sm font-bold text-blue-700 shadow-sm hover:bg-blue-50 transition-all"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Connect Google Calendar
              </button>
            </div>

            <div className="flex justify-center pt-2">
              <button
                onClick={() => navigate('/meetingscalendar')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 to-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-md hover:from-sky-600 hover:to-indigo-700 transition-all"
              >
                Open Meetings →
              </button>
            </div>
          </div>
        </div>
      );
    }

    /* ── 11: Holiday ───────────────────────────────────────────────────────── */
    if (step === 11) {
      return (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
          {/* Tropical animated header */}
          <div className="relative overflow-hidden bg-gradient-to-b from-yellow-300 via-amber-400 to-sky-400 px-6 py-12 text-white sm:px-8">
            {/* Animated glowing sun */}
            <motion.div
              className="pointer-events-none absolute left-1/2 -top-8 h-28 w-28 -translate-x-1/2 rounded-full bg-yellow-300"
              style={{ boxShadow: '0 0 0 8px rgba(253,224,71,0.35), 0 0 0 20px rgba(253,224,71,0.18), 0 0 50px 24px rgba(253,224,71,0.55)' }}
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />
            {/* Animated halo rings */}
            <motion.div
              className="pointer-events-none absolute left-1/2 -top-8 h-28 w-28 -translate-x-1/2 rounded-full border-4 border-yellow-200/50"
              animate={{ scale: [1, 1.6, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeOut' }}
            />
            {/* Palm trees */}
            <div className="pointer-events-none absolute bottom-0 left-3 text-6xl leading-none select-none">🌴</div>
            <div className="pointer-events-none absolute bottom-0 right-3 text-6xl leading-none select-none" style={{ transform: 'scaleX(-1)' }}>🌴</div>
            <div className="relative z-10 text-center pt-12">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/30 px-3 py-1 text-xs font-semibold uppercase tracking-widest backdrop-blur-sm text-amber-900"
              >
                🏖️ Leave Management
              </motion.div>
              <h2 className="text-2xl font-extrabold drop-shadow sm:text-3xl text-amber-900">Holiday Booking</h2>
              <p className="mt-2 text-sm text-amber-800 sm:text-base">Request time off, check your entitlement, and manage absence — all in one place.</p>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: '☀️', label: 'Full Day',      desc: 'A full working day off.' },
                { icon: '🌤️', label: 'Half Day',     desc: 'AM or PM leave.' },
                { icon: '⏱️', label: 'Hours',         desc: 'Book specific hours.' },
                { icon: '📆', label: 'Block Booking', desc: 'Multiple consecutive days.' },
              ].map(({ icon, label, desc }) => (
                <div key={label} className="flex flex-col items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-center shadow-sm">
                  <span className="text-2xl">{icon}</span>
                  <p className="text-xs font-bold text-amber-900">{label}</p>
                  <p className="text-[10px] text-amber-700 leading-tight">{desc}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-sky-100 bg-sky-50/60 p-5">
              <div className="flex items-start gap-3">
                <span className="text-xl shrink-0">📊</span>
                <div>
                  <p className="text-sm font-bold text-sky-900">Checking Your Entitlement</p>
                  <p className="mt-1 text-sm text-sky-800">
                    The holiday form shows your <strong>full yearly allowance</strong>, hours/days <strong>already taken</strong>, and your <strong>remaining balance</strong>. The system warns you if a request exceeds your entitlement.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-4 flex items-start gap-3">
              <span className="text-xl shrink-0">🚨</span>
              <div>
                <p className="text-sm font-bold text-rose-900">Emergency Leave</p>
                <p className="text-xs text-rose-700 mt-0.5">Mark a request as emergency leave by selecting <strong>Yes</strong> in the Emergency Leave field. Your manager will be notified immediately.</p>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
              <p className="text-sm font-bold text-gray-800 mb-3">How to submit a request</p>
              <ol className="space-y-1.5 text-sm text-gray-600 list-decimal list-inside">
                <li>Go to <strong>Holiday</strong> in the navigation menu.</li>
                <li>Choose your holiday length — Full Day, Half Day, Hours, or Block.</li>
                <li>Set your start and end dates and add a reason.</li>
                <li>Click <strong>Submit Request</strong> — your manager will be notified for approval.</li>
              </ol>
            </div>

            <div className="flex justify-center pt-2">
              <button
                onClick={() => navigate('/staffholiday')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3 text-sm font-bold text-white shadow-md hover:from-amber-600 hover:to-orange-600 transition-all"
              >
                Book a Holiday →
              </button>
            </div>
          </div>
        </div>
      );
    }

    /* ── 12: Get Started ─────────────────────────────────────────────────── */
    return (
      <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
        <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 px-8 py-16 text-white">
          <motion.div className="absolute -bottom-10 -right-10 h-72 w-72 rounded-full bg-white/10 blur-3xl"
            animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }} />
          <div className="relative z-10 text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-white/20 shadow-xl backdrop-blur-sm"
            >
              <RocketLaunchIcon className="h-10 w-10 text-white" />
            </motion.div>
            <h2 className="text-4xl font-extrabold">You're all set!</h2>
            <p className="mt-4 text-lg text-indigo-100">Welcome to {companyName}. Everything is ready for you.</p>
          </div>
        </div>
        <div className="p-8">
          <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
            <video
              src="https://cdn.insightos.co.uk/Welcome%20To%20The%20Future%20Of%20Work%20(2).mp4"
              controls preload="metadata" poster="/1.png"
              className="w-full max-h-[55vh] object-contain"
            />
          </div>
        </div>
      </div>
    );
  };

  const progressPct = ((step + 1) / totalSteps) * 100;

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -left-24 top-0    h-80 w-80 rounded-full bg-indigo-300/30 blur-3xl" />
        <div className="absolute right-0  top-24   h-80 w-80 rounded-full bg-violet-300/30 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-sky-300/20    blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-5xl">
        {/* ── Progress header ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6 overflow-hidden rounded-3xl border border-white/60 bg-white/80 shadow-lg backdrop-blur-sm"
        >
          {/* Rainbow progress bar */}
          <div className="h-1.5 bg-gray-100">
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-500 via-violet-500 to-pink-500"
              animate={{ width: `${progressPct}%` }}
              initial={{ width: 0 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-500">Staff Onboarding</p>
              <h2 className="mt-0.5 text-lg font-extrabold text-gray-900 sm:text-xl">{onboardingSteps[step]}</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-gradient-to-r from-indigo-100 to-violet-100 px-3 py-1 text-xs font-bold text-indigo-700">
                {step + 1} / {totalSteps}
              </span>
              <span className="text-xs font-semibold text-gray-400">{Math.round(progressPct)}%</span>
            </div>
          </div>
        </motion.div>

        {/* ── Top nav bar ── */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <button
            onClick={() => go(step - 1)}
            disabled={step === 0}
            className="rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 transition-colors backdrop-blur-sm"
          >
            ← Back
          </button>

          <span className="rounded-full bg-gradient-to-r from-indigo-100 to-violet-100 px-3 py-1 text-xs font-bold text-indigo-700 whitespace-nowrap">
            {step + 1} / {totalSteps}
          </span>

          {step < totalSteps - 1 ? (
            <button
              onClick={() => go(step + 1)}
              className="rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-blue-600 px-5 py-2 text-sm font-bold text-white shadow-md hover:from-indigo-700 hover:via-violet-700 hover:to-blue-700 transition-all"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={completeOnboarding}
              disabled={savingComplete}
              className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-2 text-sm font-bold text-white shadow-md hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 transition-all"
            >
              {savingComplete ? 'Saving…' : '🎉 Complete'}
            </button>
          )}
        </div>

        {/* ── Step body ── */}
        <div className="min-h-[480px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div key={step} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>

      </div>

      {/* ── Saving modal ── */}
      {savingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-8 text-white text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-white/20">
                <CheckBadgeIcon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold">Onboarding Complete!</h3>
              <p className="mt-1 text-sm text-indigo-100">We are saving your data.</p>
            </div>
            <div className="p-6 text-center">
              <p className="text-sm text-gray-700">Please log back in again to access your full dashboard.</p>
              <button
                onClick={() => { setSavingModal(false); navigate('/'); }}
                className="mt-5 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-sm font-bold text-white shadow-md hover:from-indigo-700 hover:to-violet-700 transition-all"
              >
                Log Back In →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
